import type { TabelaSchematu, WezelBazy, WierszWyrownania, WynikZapytania } from '@/lib/baza-api';
import type { WierszPorownania } from '@/lib/porownanie-wynikow';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  NAPRAWY Z PORÓWNANIA — „Wyrównaj do …" / „Przyjmij z tej bazy" (etap 2b, 28.09.2026).
 *
 *  ⚠️⚠️ KIERUNEK WYBIERA CZŁOWIEK, a zmiana i tak idzie na CAŁĄ WYSPĘ (master pierwszy) —
 *  tak ustalone przy makiecie: „musi być tak, żeby potem nie było rozjazdów".
 *    • `DO_DRUGIEJ` — prawdą jest baza porównania (zwykle master): jej wersja jedzie wszędzie;
 *    • `Z_TEJ` — prawdą jest oglądana baza (np. ktoś poprawił wiersz w SPSUI na maszynie).
 *
 *  ⚠️⚠️ DLA TABEL AUTOMATU WZORCEM JEST BAZA JEGO MASZYNY. Kierunek, którego źródłem nie jest
 *  właściciel automatu z tego wiersza, nie jest proponowany wcale — serwer i tak by odmówił.
 *  ⚠️ USUNIĘCIE (od 29.09.2026) TYLKO W TABELACH-LIŚCIACH: „tylko tu" wg drugiej bazy i „brak tu"
 *  wg tej to wiersz, którego baza-wzorzec nie ma — znika z całej wyspy. W tabeli nadrzędnej
 *  (katalog bez `D`) tego kierunku nie ma wcale, jak dawniej.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

export type Kierunek = 'DO_DRUGIEJ' | 'Z_TEJ';

export type Naprawa = {
  kierunek: Kierunek;
  zrodlo: WezelBazy;
  wiersz: WierszWyrownania;
  /** Naprawa usuwa wiersz z całej wyspy — ekran pyta o to osobno przy wysyłce. */
  usuwa?: true;
};

export function mozliweNaprawy(
  w: WierszPorownania,
  ctx: {
    wynik: WynikZapytania;
    tabela: TabelaSchematu;
    tu: WezelBazy;
    tam: WezelBazy;
    wezly: readonly WezelBazy[];
    /** Akcje katalogu edytora dla tej tabeli — `D` tylko w liściach. */
    akcje: readonly string[];
  },
): Naprawa[] {
  const { wynik, tabela, tu, tam } = ctx;
  const usuwanie = ctx.akcje.includes('D');
  const nazwy = wynik.kolumny.map((k) => k.nazwa);
  const jakoWartosci = (r: (string | null)[]): Record<string, string | null> =>
    Object.fromEntries(nazwy.map((n, i) => [n, r[i] ?? null]));

  let opcje: Naprawa[] = [];
  if (w.stan === 'INNY' && w.tam !== undefined && ctx.akcje.includes('P')) {
    const zapisywalne = new Set(tabela.kolumny.filter((k) => k.zapisywalna).map((k) => k.nazwa.toLowerCase()));
    const kolumny = (w.rozne ?? []).map((i) => nazwy[i]!).filter((n) => zapisywalne.has(n.toLowerCase()));
    /** ⚠️ Różnica tylko poza bramką (stemple, stan maszyny) — nie ma czego wyrównywać. */
    if (kolumny.length === 0) return [];
    opcje = [
      { kierunek: 'DO_DRUGIEJ', zrodlo: tam, wiersz: { rodzaj: 'INNY', wartosci: jakoWartosci(w.tam), kolumny } },
      { kierunek: 'Z_TEJ', zrodlo: tu, wiersz: { rodzaj: 'INNY', wartosci: jakoWartosci(w.wiersz), kolumny } },
    ];
  } else if (w.stan === 'BRAK_TU') {
    opcje = [{ kierunek: 'DO_DRUGIEJ', zrodlo: tam, wiersz: { rodzaj: 'BRAK', wartosci: jakoWartosci(w.wiersz) } }];
    /** Wg tej bazy: tu wiersza nie ma, więc znika z całej wyspy. */
    if (usuwanie) opcje.push({ kierunek: 'Z_TEJ', zrodlo: tu, wiersz: { rodzaj: 'NADMIAR', wartosci: jakoWartosci(w.wiersz) }, usuwa: true });
  } else if (w.stan === 'TYLKO_TU') {
    opcje = [{ kierunek: 'Z_TEJ', zrodlo: tu, wiersz: { rodzaj: 'BRAK', wartosci: jakoWartosci(w.wiersz) } }];
    /** Wg drugiej bazy: tam wiersza nie ma, więc znika z całej wyspy. */
    if (usuwanie) opcje.push({ kierunek: 'DO_DRUGIEJ', zrodlo: tam, wiersz: { rodzaj: 'NADMIAR', wartosci: jakoWartosci(w.wiersz) }, usuwa: true });
  }

  if (!tabela.automatu) return opcje;
  const iVmid = nazwy.findIndex((n) => n.toLowerCase() === 'vmid');
  const vmid = iVmid < 0 ? NaN : Number(w.wiersz[iVmid]);
  const wlasciciel = ctx.wezly.find((n) => n.spsVmid !== null && n.spsVmid === vmid);
  return wlasciciel === undefined ? [] : opcje.filter((o) => o.zrodlo.id === wlasciciel.id);
}

/** Właściciel automatu z wiersza — do dymka „wzorcem jest baza …", gdy żadna naprawa nie przysługuje. */
export function wlascicielWiersza(
  w: WierszPorownania,
  wynik: WynikZapytania,
  wezly: readonly WezelBazy[],
): WezelBazy | null {
  const iVmid = wynik.kolumny.findIndex((k) => k.nazwa.toLowerCase() === 'vmid');
  if (iVmid < 0) return null;
  const vmid = Number(w.wiersz[iVmid]);
  return wezly.find((n) => n.spsVmid !== null && n.spsVmid === vmid) ?? null;
}
